import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card,
  CardContent, CardActionArea, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, LinearProgress, Alert, IconButton,
  Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Rating, Accordion, AccordionSummary, AccordionDetails, Divider,
} from '@mui/material';
import {
  TrendingUp, Add, Edit, Delete, Close, EmojiEvents, Star,
  CheckCircle, ExpandMore, WorkspacePremium, Timeline, Person,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CONFIG: Record<string, { label: string; color: any }> = {
  in_progress: { label: 'In Progress', color: 'default' },
  ready:       { label: 'Ready to Promote', color: 'success' },
  promoted:    { label: 'Promoted ✓', color: 'primary' },
  not_ready:   { label: 'Not Ready', color: 'warning' },
};

function fmtDate(s?: string) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtSalary(v?: number) {
  if (!v) return '';
  return `HK$${Number(v).toLocaleString()}`;
}

function ReadinessBar({ pct }: { pct: number }) {
  const color = pct >= 80 ? '#2e7d32' : pct >= 60 ? '#f57c00' : '#c62828';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, height: 8, bgcolor: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </Box>
      <Typography variant="caption" fontWeight={700} color={color}>{pct}%</Typography>
    </Box>
  );
}

interface Track { id: string; name: string; description?: string; emoji?: string; color?: string; isActive?: boolean; levels?: Level[]; }
interface Level { id: string; trackId: string; code: string; title: string; order: number; scope?: string; typicalYearsMin?: number; typicalYearsMax?: number; salaryMin?: number; salaryMax?: number; coreExpectations?: string; keySkills?: string; leadershipExpectations?: string; promotionCriteria?: string; track?: Track; }
interface Assessment { id: string; employeeId: string; levelId: string; assessorId?: string; status: string; readinessPercent: number; technicalScore?: number; deliveryScore?: number; communicationScore?: number; leadershipScore?: number; growthScore?: number; strengths?: string; developmentAreas?: string; managerNotes?: string; targetPromotionDate?: string; promotedAt?: string; assessmentDate: string; employee?: { id: string; firstName: string; lastName: string; department?: { name: string } }; level?: Level; assessor?: { firstName: string; lastName: string }; }
interface Stats { totalTracks: number; totalLevels: number; totalAssessed: number; readyForPromotion: number; promoted: number; avgReadiness: number; byTrack: Array<{ name: string; count: number }>; }

export default function CareerPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');
  const isManager = ['hr_admin', 'hr_manager', 'manager'].includes(user?.role ?? '');

  const [tab, setTab] = useState(0);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [trackDialog, setTrackDialog] = useState<Partial<Track> | null>(null);
  const [levelDialog, setLevelDialog] = useState<Partial<Level> | null>(null);
  const [assessDialog, setAssessDialog] = useState<Partial<Assessment> | null>(null);
  const [detailDialog, setDetailDialog] = useState<Assessment | null>(null);
  const [promoteDialog, setPromoteDialog] = useState<Assessment | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [t, a, s] = await Promise.all([
        apiFetch('/api/career/tracks'),
        apiFetch('/api/career/assessments'),
        apiFetch('/api/career/stats'),
      ]);
      setTracks(t); setAssessments(a); setStats(s);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isManager && !employees.length)
      apiFetch('/api/employees?limit=500').then(r => setEmployees(r.data || r || [])).catch(() => {});
  }, [isManager, employees.length]);

  // Flatten all levels from all tracks
  const allLevels = tracks.flatMap(t => (t.levels ?? []).map(l => ({ ...l, track: t })));

  const saveTrack = async () => {
    if (!trackDialog) return;
    try {
      trackDialog.id
        ? await apiFetch(`/api/career/tracks/${trackDialog.id}`, { method: 'PATCH', body: JSON.stringify(trackDialog) })
        : await apiFetch('/api/career/tracks', { method: 'POST', body: JSON.stringify(trackDialog) });
      setTrackDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const deleteTrack = async (id: string) => {
    if (!window.confirm('Delete this track and all its levels?')) return;
    try { await apiFetch(`/api/career/tracks/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const saveLevel = async () => {
    if (!levelDialog) return;
    try {
      levelDialog.id
        ? await apiFetch(`/api/career/levels/${levelDialog.id}`, { method: 'PATCH', body: JSON.stringify(levelDialog) })
        : await apiFetch('/api/career/levels', { method: 'POST', body: JSON.stringify(levelDialog) });
      setLevelDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const deleteLevel = async (id: string) => {
    if (!window.confirm('Delete this level?')) return;
    try { await apiFetch(`/api/career/levels/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const saveAssessment = async () => {
    if (!assessDialog) return;
    try {
      assessDialog.id
        ? await apiFetch(`/api/career/assessments/${assessDialog.id}`, { method: 'PATCH', body: JSON.stringify(assessDialog) })
        : await apiFetch('/api/career/assessments', { method: 'POST', body: JSON.stringify({ ...assessDialog, assessmentDate: today }) });
      setAssessDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const markReady = async (a: Assessment) => {
    try {
      await apiFetch(`/api/career/assessments/${a.id}/ready`, { method: 'POST', body: JSON.stringify({ targetDate: a.targetPromotionDate }) });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const markPromoted = async (id: string) => {
    try { await apiFetch(`/api/career/assessments/${id}/promoted`, { method: 'POST' }); setPromoteDialog(null); load(); }
    catch (e: any) { setError(e.message); }
  };

  const deleteAssessment = async (id: string) => {
    if (!window.confirm('Delete this assessment?')) return;
    try { await apiFetch(`/api/career/assessments/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const readyCount = assessments.filter(a => a.status === 'ready').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp sx={{ fontSize: 32, color: '#1565c0' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Career Path Framework</Typography>
            <Typography variant="body2" color="text.secondary">Define career tracks, level expectations, and assess employee readiness</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="outlined" startIcon={<Add />}
            onClick={() => setTrackDialog({ isActive: true })}>
            Add Track
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Career Tracks', value: stats.totalTracks,       icon: <Timeline />,        color: '#1565c0' },
            { label: 'Total Levels',  value: stats.totalLevels,        icon: <WorkspacePremium />, color: '#6a1b9a' },
            { label: 'Assessed',      value: stats.totalAssessed,       icon: <Person />,          color: '#2e7d32' },
            { label: 'Ready to Promote', value: stats.readyForPromotion, icon: <Star />,          color: readyCount > 0 ? '#f57c00' : '#2e7d32' },
            { label: 'Promoted YTD', value: stats.promoted,            icon: <EmojiEvents />,     color: '#c62828' },
            { label: 'Avg Readiness', value: `${stats.avgReadiness}%`, icon: <CheckCircle />,     color: '#00695c' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 4, md: 2 }}>
              <Card sx={{ textAlign: 'center', p: 0.5 }}>
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                  <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {readyCount > 0 && (
        <Alert severity="success" icon={<EmojiEvents />} sx={{ mb: 2 }}>
          <Typography fontWeight={700}>{readyCount} employee{readyCount > 1 ? 's' : ''} ready for promotion</Typography>
          {assessments.filter(a => a.status === 'ready').map(a => (
            <Typography key={a.id} variant="body2">
              • {a.employee?.firstName} {a.employee?.lastName} → {a.level?.title} ({a.level?.track?.name})
              {a.targetPromotionDate && ` — target ${fmtDate(a.targetPromotionDate)}`}
            </Typography>
          ))}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          <Tab label="Career Tracks" />
          <Tab label={`Assessments (${assessments.length})`} />
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── TRACKS & LEVELS ── */}
      {tab === 0 && (
        <Box>
          {tracks.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <TrendingUp sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>No career tracks defined yet.</Typography>
              {isHR && <Button sx={{ mt: 1 }} onClick={() => setTrackDialog({ isActive: true })}>Create First Track</Button>}
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {tracks.map(track => (
                <Grid key={track.id} size={{ xs: 12, md: 6 }}>
                  <Card sx={{ border: `2px solid ${track.color ?? '#e0e0e0'}20` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h5">{track.emoji}</Typography>
                          <Box>
                            <Typography fontWeight={700}>{track.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{track.description}</Typography>
                          </Box>
                        </Box>
                        {isHR && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Add Level">
                              <IconButton size="small" color="primary"
                                onClick={() => setLevelDialog({ trackId: track.id, order: (track.levels?.length ?? 0) + 1 })}>
                                <Add fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => setTrackDialog({ ...track })}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteTrack(track.id)}><Delete fontSize="small" /></IconButton>
                          </Box>
                        )}
                      </Box>

                      {(track.levels ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No levels yet.</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {(track.levels ?? []).sort((a, b) => a.order - b.order).map(level => (
                            <Box key={level.id}
                              sx={{
                                p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0',
                                cursor: 'pointer',
                                bgcolor: selectedLevel?.id === level.id ? `${track.color ?? '#1565c0'}15` : '#fafafa',
                                '&:hover': { bgcolor: `${track.color ?? '#1565c0'}10` },
                              }}
                              onClick={() => setSelectedLevel(selectedLevel?.id === level.id ? null : { ...level, track })}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Chip label={level.code} size="small"
                                      sx={{ bgcolor: track.color ?? '#1565c0', color: '#fff', fontSize: '0.7rem', fontWeight: 700 }} />
                                    <Typography fontWeight={600}>{level.title}</Typography>
                                  </Box>
                                  {level.typicalYearsMin !== null && level.typicalYearsMin !== undefined && (
                                    <Typography variant="caption" color="text.secondary">
                                      {level.typicalYearsMin}–{level.typicalYearsMax === 999 ? '∞' : level.typicalYearsMax} yrs ·{' '}
                                      {level.salaryMin && fmtSalary(level.salaryMin)} – {level.salaryMax && fmtSalary(level.salaryMax)}/mo
                                    </Typography>
                                  )}
                                </Box>
                                {isHR && (
                                  <Box sx={{ display: 'flex', gap: 0.5 }} onClick={e => e.stopPropagation()}>
                                    <IconButton size="small" onClick={() => setLevelDialog({ ...level })}><Edit sx={{ fontSize: 14 }} /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => deleteLevel(level.id)}><Delete sx={{ fontSize: 14 }} /></IconButton>
                                  </Box>
                                )}
                              </Box>

                              {selectedLevel?.id === level.id && (
                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                  {level.scope && <Typography variant="body2" sx={{ mb: 1 }}>{level.scope}</Typography>}
                                  {level.coreExpectations && (
                                    <Box sx={{ mb: 1 }}>
                                      <Typography variant="caption" fontWeight={700} color="text.secondary">CORE EXPECTATIONS</Typography>
                                      {level.coreExpectations.split(';').map((e, i) => (
                                        <Typography key={i} variant="body2">• {e.trim()}</Typography>
                                      ))}
                                    </Box>
                                  )}
                                  {level.keySkills && (
                                    <Box sx={{ mb: 1 }}>
                                      <Typography variant="caption" fontWeight={700} color="text.secondary">KEY SKILLS</Typography>
                                      <Typography variant="body2">{level.keySkills}</Typography>
                                    </Box>
                                  )}
                                  {level.promotionCriteria && (
                                    <Box sx={{ p: 1, bgcolor: '#fff8e1', borderRadius: 1, mt: 1 }}>
                                      <Typography variant="caption" fontWeight={700} color="#f57c00">PROMOTION CRITERIA →</Typography>
                                      <Typography variant="body2">{level.promotionCriteria}</Typography>
                                    </Box>
                                  )}
                                  {isManager && (
                                    <Button size="small" sx={{ mt: 1 }} variant="outlined"
                                      onClick={() => setAssessDialog({ levelId: level.id, status: 'in_progress' })}>
                                      + Assess Employee
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ── ASSESSMENTS ── */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            {isManager && (
              <Button variant="contained" startIcon={<Add />}
                onClick={() => setAssessDialog({ status: 'in_progress' })}>
                New Assessment
              </Button>
            )}
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><b>Employee</b></TableCell>
                  <TableCell><b>Track · Level</b></TableCell>
                  <TableCell><b>Readiness</b></TableCell>
                  <TableCell><b>Scores</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Target Date</b></TableCell>
                  <TableCell><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessments.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{a.employee?.firstName} {a.employee?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.employee?.department?.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{a.level?.track?.name}</Typography>
                      <Chip label={`${a.level?.code} ${a.level?.title}`} size="small"
                        sx={{ bgcolor: a.level?.track?.color ?? '#90a4ae', color: '#fff', fontSize: '0.65rem', mt: 0.5 }} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <ReadinessBar pct={a.readinessPercent} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {[
                          { k: 'Tech', v: a.technicalScore },
                          { k: 'Del', v: a.deliveryScore },
                          { k: 'Comm', v: a.communicationScore },
                          { k: 'Lead', v: a.leadershipScore },
                          { k: 'Growth', v: a.growthScore },
                        ].filter(s => s.v).map(s => (
                          <Tooltip key={s.k} title={s.k}>
                            <Chip label={`${s.v}/5`} size="small" sx={{ fontSize: '0.6rem' }} />
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_CONFIG[a.status]?.label ?? a.status} size="small"
                        color={STATUS_CONFIG[a.status]?.color ?? 'default'} />
                    </TableCell>
                    <TableCell>{fmtDate(a.targetPromotionDate)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Detail">
                        <IconButton size="small" onClick={() => setDetailDialog(a)}><TrendingUp fontSize="small" /></IconButton>
                      </Tooltip>
                      {isManager && (
                        <>
                          <IconButton size="small" onClick={() => setAssessDialog({ ...a })}><Edit fontSize="small" /></IconButton>
                          {a.status === 'in_progress' && a.readinessPercent >= 70 && (
                            <Tooltip title="Mark Ready">
                              <IconButton size="small" color="success" onClick={() => markReady(a)}><Star fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                          {a.status === 'ready' && (
                            <Button size="small" variant="contained" color="success" sx={{ ml: 0.5 }}
                              onClick={() => setPromoteDialog(a)}>Promote</Button>
                          )}
                          <IconButton size="small" color="error" onClick={() => deleteAssessment(a.id)}><Delete fontSize="small" /></IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {assessments.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No assessments yet. Open the Career Tracks tab and click "Assess Employee" on a level.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── TRACK DIALOG ── */}
      <Dialog open={!!trackDialog} onClose={() => setTrackDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {trackDialog?.id ? 'Edit Track' : 'New Career Track'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setTrackDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Emoji" sx={{ width: 80 }} value={trackDialog?.emoji ?? ''}
                onChange={e => setTrackDialog(p => ({ ...p, emoji: e.target.value }))} />
              <TextField label="Track Name" fullWidth required value={trackDialog?.name ?? ''}
                onChange={e => setTrackDialog(p => ({ ...p, name: e.target.value }))} />
            </Box>
            <TextField label="Description" fullWidth value={trackDialog?.description ?? ''}
              onChange={e => setTrackDialog(p => ({ ...p, description: e.target.value }))} />
            <TextField label="Color (hex)" fullWidth value={trackDialog?.color ?? ''}
              onChange={e => setTrackDialog(p => ({ ...p, color: e.target.value }))}
              placeholder="#1565c0" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveTrack} disabled={!trackDialog?.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── LEVEL DIALOG ── */}
      <Dialog open={!!levelDialog} onClose={() => setLevelDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {levelDialog?.id ? 'Edit Level' : 'New Level'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setLevelDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {!levelDialog?.trackId && (
              <TextField select label="Track" fullWidth value={levelDialog?.trackId ?? ''}
                onChange={e => setLevelDialog(p => ({ ...p, trackId: e.target.value }))}>
                {tracks.map(t => <MenuItem key={t.id} value={t.id}>{t.emoji} {t.name}</MenuItem>)}
              </TextField>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Code" sx={{ width: 100 }} required value={levelDialog?.code ?? ''}
                onChange={e => setLevelDialog(p => ({ ...p, code: e.target.value }))} placeholder="L3" />
              <TextField label="Title" fullWidth required value={levelDialog?.title ?? ''}
                onChange={e => setLevelDialog(p => ({ ...p, title: e.target.value }))} />
              <TextField label="Order" type="number" sx={{ width: 80 }} value={levelDialog?.order ?? 1}
                onChange={e => setLevelDialog(p => ({ ...p, order: Number(e.target.value) }))} />
            </Box>
            <TextField label="Scope" fullWidth value={levelDialog?.scope ?? ''}
              onChange={e => setLevelDialog(p => ({ ...p, scope: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Min Yrs" type="number" fullWidth value={levelDialog?.typicalYearsMin ?? ''}
                onChange={e => setLevelDialog(p => ({ ...p, typicalYearsMin: Number(e.target.value) }))} />
              <TextField label="Max Yrs" type="number" fullWidth value={levelDialog?.typicalYearsMax ?? ''}
                onChange={e => setLevelDialog(p => ({ ...p, typicalYearsMax: Number(e.target.value) }))} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Salary Min (HKD/mo)" type="number" fullWidth value={levelDialog?.salaryMin ?? ''}
                onChange={e => setLevelDialog(p => ({ ...p, salaryMin: Number(e.target.value) }))} />
              <TextField label="Salary Max (HKD/mo)" type="number" fullWidth value={levelDialog?.salaryMax ?? ''}
                onChange={e => setLevelDialog(p => ({ ...p, salaryMax: Number(e.target.value) }))} />
            </Box>
            <TextField label="Core Expectations (semicolon-separated)" fullWidth multiline rows={3}
              value={levelDialog?.coreExpectations ?? ''}
              onChange={e => setLevelDialog(p => ({ ...p, coreExpectations: e.target.value }))} />
            <TextField label="Key Skills" fullWidth multiline rows={2} value={levelDialog?.keySkills ?? ''}
              onChange={e => setLevelDialog(p => ({ ...p, keySkills: e.target.value }))} />
            <TextField label="Leadership Expectations" fullWidth multiline rows={2} value={levelDialog?.leadershipExpectations ?? ''}
              onChange={e => setLevelDialog(p => ({ ...p, leadershipExpectations: e.target.value }))} />
            <TextField label="Promotion Criteria (to get to the next level)" fullWidth multiline rows={2} value={levelDialog?.promotionCriteria ?? ''}
              onChange={e => setLevelDialog(p => ({ ...p, promotionCriteria: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLevelDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveLevel} disabled={!levelDialog?.code || !levelDialog?.title}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── ASSESS DIALOG ── */}
      <Dialog open={!!assessDialog} onClose={() => setAssessDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {assessDialog?.id ? 'Edit Assessment' : 'New Career Assessment'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setAssessDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField select label="Employee" fullWidth required value={assessDialog?.employeeId ?? ''}
              onChange={e => setAssessDialog(p => ({ ...p, employeeId: e.target.value }))}>
              {employees.map((e: any) => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>)}
            </TextField>
            <TextField select label="Career Level" fullWidth required value={assessDialog?.levelId ?? ''}
              onChange={e => setAssessDialog(p => ({ ...p, levelId: e.target.value }))}>
              {allLevels.sort((a, b) => a.track.name.localeCompare(b.track.name) || a.order - b.order).map(l => (
                <MenuItem key={l.id} value={l.id}>{l.track.emoji} {l.track.name} — {l.code} {l.title}</MenuItem>
              ))}
            </TextField>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">DIMENSION SCORES (1–5)</Typography>
            {[
              { key: 'technicalScore', label: 'Technical' },
              { key: 'deliveryScore', label: 'Delivery' },
              { key: 'communicationScore', label: 'Communication' },
              { key: 'leadershipScore', label: 'Leadership' },
              { key: 'growthScore', label: 'Growth Mindset' },
            ].map(dim => (
              <Box key={dim.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ width: 140 }} variant="body2">{dim.label}</Typography>
                <Rating value={(assessDialog as any)?.[dim.key] ?? 0}
                  onChange={(_, v) => setAssessDialog(p => ({ ...p, [dim.key]: v }))} />
                <Typography variant="caption" color="text.secondary">
                  {(assessDialog as any)?.[dim.key] ? `${(assessDialog as any)[dim.key]}/5` : '—'}
                </Typography>
              </Box>
            ))}
            <TextField label="Strengths" fullWidth multiline rows={2} value={assessDialog?.strengths ?? ''}
              onChange={e => setAssessDialog(p => ({ ...p, strengths: e.target.value }))} />
            <TextField label="Development Areas" fullWidth multiline rows={2} value={assessDialog?.developmentAreas ?? ''}
              onChange={e => setAssessDialog(p => ({ ...p, developmentAreas: e.target.value }))} />
            <TextField label="Manager Notes" fullWidth multiline rows={2} value={assessDialog?.managerNotes ?? ''}
              onChange={e => setAssessDialog(p => ({ ...p, managerNotes: e.target.value }))} />
            <TextField label="Target Promotion Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={assessDialog?.targetPromotionDate ?? ''}
              onChange={e => setAssessDialog(p => ({ ...p, targetPromotionDate: e.target.value || undefined }))} />
            <TextField select label="Status" fullWidth value={assessDialog?.status ?? 'in_progress'}
              onChange={e => setAssessDialog(p => ({ ...p, status: e.target.value }))}>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssessDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveAssessment}
            disabled={!assessDialog?.employeeId || !assessDialog?.levelId}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── DETAIL DIALOG ── */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Career Assessment — {detailDialog?.employee?.firstName} {detailDialog?.employee?.lastName}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setDetailDialog(null)}><Close /></IconButton>
        </DialogTitle>
        {detailDialog && (
          <DialogContent dividers>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={detailDialog.level?.track?.name} sx={{ bgcolor: detailDialog.level?.track?.color ?? '#90a4ae', color: '#fff' }} />
              <Chip label={`${detailDialog.level?.code} ${detailDialog.level?.title}`} />
              <Chip label={STATUS_CONFIG[detailDialog.status]?.label} color={STATUS_CONFIG[detailDialog.status]?.color} />
            </Box>
            <Typography variant="h4" fontWeight={700} color={detailDialog.readinessPercent >= 80 ? '#2e7d32' : '#f57c00'} sx={{ mb: 1 }}>
              {detailDialog.readinessPercent}% Ready
            </Typography>
            <ReadinessBar pct={detailDialog.readinessPercent} />
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[
                { k: 'Technical', v: detailDialog.technicalScore },
                { k: 'Delivery', v: detailDialog.deliveryScore },
                { k: 'Communication', v: detailDialog.communicationScore },
                { k: 'Leadership', v: detailDialog.leadershipScore },
                { k: 'Growth', v: detailDialog.growthScore },
              ].map(s => (
                <Grid key={s.k} size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{s.k}</Typography>
                  <Rating value={s.v ?? 0} readOnly size="small" />
                </Grid>
              ))}
            </Grid>
            {detailDialog.strengths && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">STRENGTHS</Typography>
                <Typography variant="body2">{detailDialog.strengths}</Typography>
              </Box>
            )}
            {detailDialog.developmentAreas && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">DEVELOPMENT AREAS</Typography>
                <Typography variant="body2">{detailDialog.developmentAreas}</Typography>
              </Box>
            )}
            {detailDialog.managerNotes && (
              <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">MANAGER NOTES</Typography>
                <Typography variant="body2">{detailDialog.managerNotes}</Typography>
              </Box>
            )}
            {detailDialog.targetPromotionDate && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                🎯 Target promotion: <b>{fmtDate(detailDialog.targetPromotionDate)}</b>
              </Typography>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Close</Button>
          {isManager && detailDialog?.status !== 'promoted' && (
            <Button variant="contained" onClick={() => { setAssessDialog({ ...detailDialog! }); setDetailDialog(null); }}>Edit</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── PROMOTE CONFIRM ── */}
      <Dialog open={!!promoteDialog} onClose={() => setPromoteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Promotion</DialogTitle>
        <DialogContent>
          <Typography>
            Mark <b>{promoteDialog?.employee?.firstName} {promoteDialog?.employee?.lastName}</b> as promoted to{' '}
            <b>{promoteDialog?.level?.title}</b>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will set their assessment status to "Promoted" with today's date.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => markPromoted(promoteDialog!.id)}>
            Confirm Promotion 🎉
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
